import { roundQuantity } from '../schedule/unit-of-measure';
import {
  groundBeamBottom,
  groundBeamFreeLength,
  groundBeamSideFace,
  groundBeamVolume,
  pileCapBottom,
  pileCapSideFace,
  pileCapVolume,
  type GroundBeam,
  type PileCap,
} from './foundation';
import { isValidOutline } from './polygon';

/**
 * O que a conta tira dos blocos e das vigas baldrame desenhados (Fase 27, etapa 2 — D3, D6, F14,
 * RN-360, RN-367, RN-368).
 *
 * É a irmã de `pile-totals.ts`: cada peça da família da fundação tem a sua conta, e nenhuma
 * medida de AMBIENTE, PAREDE, PILAR, VIGA ou ESTACA soma o que está aqui (RN-360). As contas de
 * **cada peça** moram em `foundation.ts`, o contrato; esta é a soma delas na tipologia.
 *
 * > **A regra em uma linha (RN-368):** o baldrame é medido pelo trecho **livre** — o eixo menos
 * > o que passa dentro dos blocos **da mesma tipologia** —, e o bloco encostado no outro não
 * > tira o mesmo pedaço duas vezes.
 */

/** O concreto mora na terceira casa: o metro cúbico do bloco é pequeno, e duas comeriam o grama. */
export const FOUNDATION_VOLUME_DECIMALS = 3;

/** Faces, fundo e comprimento: duas casas, como a área e o perímetro do ambiente. */
export const FOUNDATION_AREA_DECIMALS = 2;

export interface PileCapTotals {
  /** Quantos blocos (un). */
  readonly count: number;
  /** Área em planta × altura de cada bloco, somada (m³). */
  readonly volume: number;
  /** Perímetro × altura de cada bloco — a fôrma e a impermeabilização (m²). */
  readonly faces: number;
  /** A área em planta de cada bloco — a compactação e o lastro (m²). */
  readonly bottom: number;
}

export interface GroundBeamTotals {
  /** Quantas vigas baldrame (un) — a que cabe inteira dentro de um bloco ainda é uma. */
  readonly count: number;
  /** O comprimento livre de cada uma — o eixo menos o trecho dentro dos blocos —, somado (m). */
  readonly length: number;
  /** Largura × altura × comprimento livre, somado (m³). */
  readonly volume: number;
  /** As duas faces laterais do trecho livre — a fôrma (m²). */
  readonly faces: number;
  /** Largura × comprimento livre — a compactação e o lastro (m²). */
  readonly bottom: number;
}

/** Sem bloco desenhado — o ponto de partida, e o que a tipologia sem fundação devolve. */
export const NO_PILE_CAPS: PileCapTotals = { count: 0, volume: 0, faces: 0, bottom: 0 };

/** Sem baldrame desenhado. */
export const NO_GROUND_BEAMS: GroundBeamTotals = {
  count: 0,
  length: 0,
  volume: 0,
  faces: 0,
  bottom: 0,
};

/**
 * O bloco que conta: o que não foi excluído e tem contorno de verdade.
 *
 * É o mesmo critério que o baldrame usa para descontar (`groundBeamFreeLength`): o bloco que
 * corta o baldrame é o que entra na contagem, e o contrário também.
 */
function blocoQueConta(cap: Pick<PileCap, 'outline' | 'deletedAt'>): boolean {
  return cap.deletedAt === undefined && isValidOutline(cap.outline);
}

/**
 * A conta dos blocos de uma tipologia.
 *
 * Recebe as peças **da tipologia** — o bloco é da área comum do desenho do pavimento (RN-361),
 * e quem chama já separou os dele. Soma o que cada bloco arredondou na casa dele, como a conta
 * do pilar e da viga: o total confere com a soma do que a tela mostra de cada bloco.
 */
export function pileCapTotals(caps: readonly PileCap[]): PileCapTotals {
  const contam = caps.filter(blocoQueConta);
  const soma = (valores: readonly number[], casas: number): number =>
    roundQuantity(
      valores.reduce((total, valor) => total + valor, 0),
      casas,
    );
  return {
    count: contam.length,
    volume: soma(contam.map(pileCapVolume), FOUNDATION_VOLUME_DECIMALS),
    faces: soma(contam.map(pileCapSideFace), FOUNDATION_AREA_DECIMALS),
    bottom: soma(contam.map(pileCapBottom), FOUNDATION_AREA_DECIMALS),
  };
}

/**
 * A conta das vigas baldrame de uma tipologia.
 *
 * O comprimento livre de cada uma desconta os blocos **da mesma tipologia** (RN-368): o bloco de
 * outro desenho não está na mesma planta, e descontá-lo tiraria concreto que ela tem. Quem chama
 * costuma já passar só os da tipologia; o filtro por `unitTypeId` é a regra escrita, e não
 * confiança. A ponta sem bloco não corta nada — o baldrame apoiado direto na estaca conta de
 * eixo a eixo (F14).
 */
export function groundBeamTotals(
  beams: readonly GroundBeam[],
  caps: readonly PileCap[],
): GroundBeamTotals {
  const contam = beams.filter((beam) => beam.deletedAt === undefined);
  const soma = (valores: readonly number[], casas: number): number =>
    roundQuantity(
      valores.reduce((total, valor) => total + valor, 0),
      casas,
    );

  // O bloco excluído e o sem contorno o `groundBeamFreeLength` já ignora (`blocoQueConta`).
  const livres = contam.map((beam) =>
    groundBeamFreeLength(
      beam,
      caps.filter((cap) => cap.unitTypeId === beam.unitTypeId),
    ),
  );
  return {
    count: contam.length,
    length: soma(livres, FOUNDATION_AREA_DECIMALS),
    volume: soma(
      contam.map((beam, i) => groundBeamVolume(beam, livres[i]!)),
      FOUNDATION_VOLUME_DECIMALS,
    ),
    faces: soma(
      contam.map((beam, i) => groundBeamSideFace(beam, livres[i]!)),
      FOUNDATION_AREA_DECIMALS,
    ),
    bottom: soma(
      contam.map((beam, i) => groundBeamBottom(beam, livres[i]!)),
      FOUNDATION_AREA_DECIMALS,
    ),
  };
}

/**
 * Os totais do bloco multiplicados pelos andares que repetem o desenho — o bloco de cada um
 * conta de novo, como a estaca e o shaft (RN-167). Cada número volta à casa dele; a contagem é
 * inteira.
 */
export function scalePileCapTotals(totals: PileCapTotals, times: number): PileCapTotals {
  if (times === 1) return totals;
  const area = (valor: number): number => roundQuantity(valor * times, FOUNDATION_AREA_DECIMALS);
  return {
    count: totals.count * times,
    volume: roundQuantity(totals.volume * times, FOUNDATION_VOLUME_DECIMALS),
    faces: area(totals.faces),
    bottom: area(totals.bottom),
  };
}

/** Os totais do baldrame multiplicados pelos andares — a mesma conta do bloco. */
export function scaleGroundBeamTotals(totals: GroundBeamTotals, times: number): GroundBeamTotals {
  if (times === 1) return totals;
  const area = (valor: number): number => roundQuantity(valor * times, FOUNDATION_AREA_DECIMALS);
  return {
    count: totals.count * times,
    length: area(totals.length),
    volume: roundQuantity(totals.volume * times, FOUNDATION_VOLUME_DECIMALS),
    faces: area(totals.faces),
    bottom: area(totals.bottom),
  };
}
